import { of } from 'rxjs';

import type {
  HotelIngredientAdapter,
  ItineraryResponse,
  ItinerarySearchCriteria,
  OwnStayIngredientAdapter,
  OwnTransportIngredientAdapter,
  TransportIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import { GraphQL } from '@libs/trip-planner-interfaces';
import { assertValue, exhaustiveGuard, isDefined } from '@libs/utils';
import { SemboDate } from '@libs/sembo-dates';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import { toTransportIngredientResponse } from './transportIngredientResponseQL';
import { toHotelIngredientResponse } from './hotelIngredientResponseQL';
import { toOwnStayIngredientResponse } from './ownStayIngredientResponseQL';
import { toMoney } from './moneyResponseQL';
import { toOwnTransportIngredientResponse } from './ownTransportIngredientResponseQL';

export type ItineraryResponseQL = NonNullable<
  GraphQL.searchItinerariesSubscription['searchRecipeByItinerary']
>;
export type IngredientResponseQL = NonNullable<
  ItineraryResponseQL['result']
>[number];

export function toItineraryResponse(
  itineraryResponseQL: GraphQL.searchItinerariesSubscription,
  criteria: ItinerarySearchCriteria
): ItineraryResponse {
  assertSmartSearchValue(itineraryResponseQL.searchRecipeByItinerary);
  const itineraryResponse = itineraryResponseQL.searchRecipeByItinerary;

  assertSmartSearchList(itineraryResponse.result);

  const itinerary = itineraryResponse.result.map((result, index) => {
    const currentIngredient = criteria.userCriteria.ingredients?.[index];
    assertValue(
      currentIngredient,
      'search-service-smart-search > itineraryResponseQL > currentIngredient'
    );

    switch (result.type) {
      case 'TransportIngredientResponseQL': {
        const transportIngredient =
          currentIngredient as TransportIngredientAdapter;
        return toTransportIngredientResponse(result, transportIngredient);
      }

      case 'HotelIngredientResponseQL': {
        const hotelIngredient = currentIngredient as HotelIngredientAdapter;
        return toHotelIngredientResponse(result, hotelIngredient);
      }

      case 'OwnStayResultQL': {
        const ownStayIngredient = currentIngredient as OwnStayIngredientAdapter;

        return toOwnStayIngredientResponse(result, ownStayIngredient);
      }

      case 'OwnTransportQL': {
        const ownTransportIngredient =
          currentIngredient as OwnTransportIngredientAdapter;

        return toOwnTransportIngredientResponse(result, ownTransportIngredient);
      }

      case 'TicketIngredientResponseQL':
        throw new Error('Standalone tickets not supported');

      case 'FerryIngredientResponseQL':
      case 'FlightIngredientResponseQL':
        throw new Error(
          'Specific transport ingredients not supported. Use Transport'
        );

      default:
        return exhaustiveGuard(
          result,
          'Smart Search ingredient response type not recognized'
        );
    }
  });

  const firstIngredient = itinerary[0];
  assertValue(
    firstIngredient,
    'search-service-smart-search > itinerarySearchService.ts > class ItinerarySearchService > firstIngredient'
  );

  const date = firstIngredient.date;

  assertSmartSearchValue(itineraryResponse.prices);

  return {
    date,
    itinerary: itinerary.map((ingredient) => of(ingredient)),
    prices: {
      totalPrice: itineraryResponse.prices.totalPrice
        ? toMoney(itineraryResponse.prices.totalPrice)
        : null,
      totalPriceWithAlternative: itineraryResponse.prices
        .totalPriceWithAlternative
        ? toMoney(itineraryResponse.prices.totalPriceWithAlternative)
        : null,
      grossPrice: itineraryResponse.prices.grossPrice
        ? toMoney(itineraryResponse.prices.grossPrice)
        : null,
      grossPriceWithAlternative: itineraryResponse.prices
        .grossPriceWithAlternative
        ? toMoney(itineraryResponse.prices.grossPriceWithAlternative)
        : null,
    },
    // TODO: Take value from SmartSearch response when they implement it on their side
    isSearched: isItinerarySearched(itineraryResponse, criteria, date),
  };
}

export function isItinerarySearched(
  itineraryResponse: ItineraryResponseQL,
  criteria: ItinerarySearchCriteria,
  itineraryDate: Date
): boolean {
  const isSingleDateSearch =
    criteria.businessCriteria.metaData?.salesChannel ===
      GraphQL.InputSaleChannelQL.MetaPackage ||
    criteria.businessCriteria.searchOnlyLiveSelectedDay;

  const hasPrice =
    isDefined(itineraryResponse.prices?.totalPrice) ||
    isDefined(itineraryResponse.prices?.totalPriceWithAlternative);

  const isSelectedItinerary =
    isDefined(criteria.userCriteria.selectedDate) &&
    new SemboDate(itineraryDate).isSameDay(criteria.userCriteria.selectedDate);

  const isSearchingWholeMonth = !isDefined(criteria.userCriteria.selectedDate);

  return (
    !isSingleDateSearch ||
    hasPrice ||
    isSelectedItinerary ||
    isSearchingWholeMonth
  );
}
